import React, { useState, useEffect } from 'react';
import '../styles/LoanEstimator.css';

function LoanEstimator({ loanAmount, startDate, onEstimateComplete }) {
  const [interestRate, setInterestRate] = useState(5);
  const [termLength, setTermLength] = useState(12);
  const [termType, setTermType] = useState('months');
  const [amortizationSchedule, setAmortizationSchedule] = useState([]);
  const [monthlyPayment, setMonthlyPayment] = useState(0);
  const [totalInterest, setTotalInterest] = useState(0);

  useEffect(() => {
    calculateAmortization();
  }, [loanAmount, interestRate, termLength, termType, startDate]);

  function calculateAmortization() {
    if (!loanAmount || loanAmount <= 0 || !termLength || termLength <= 0) {
      setAmortizationSchedule([]);
      setMonthlyPayment(0);
      setTotalInterest(0);
      return;
    }

    const principal = parseFloat(loanAmount);
    const annualRate = parseFloat(interestRate) / 100;
    
    // Convert term to months
    const totalMonths = termType === 'weeks' ? Math.ceil(termLength / 4.33) : termLength;
    const monthlyRate = annualRate / 12;

    // Calculate monthly payment using amortization formula
    let payment;
    if (monthlyRate === 0) {
      payment = principal / totalMonths;
    } else {
      payment = principal * (monthlyRate * Math.pow(1 + monthlyRate, totalMonths)) / 
                (Math.pow(1 + monthlyRate, totalMonths) - 1);
    }

    setMonthlyPayment(payment);

    // Generate amortization schedule with payment dates
    let balance = principal;
    let totalInterestPaid = 0;
    const schedule = [];
    
    // Calculate payment dates based on first payment date
    // Add 'T00:00:00' to ensure we're working with the correct date in local timezone
    const firstPayment = startDate ? new Date(startDate + 'T00:00:00') : new Date();

    for (let i = 0; i < totalMonths; i++) {
      const interestPayment = balance * monthlyRate;
      const principalPayment = payment - interestPayment;
      balance -= principalPayment;
      totalInterestPaid += interestPayment;

      // Calculate payment date (first payment is startDate, then add months)
      const paymentDate = new Date(firstPayment);
      paymentDate.setMonth(paymentDate.getMonth() + i);

      schedule.push({
        period: i + 1,
        paymentDate: paymentDate.toISOString().split('T')[0],
        payment: payment,
        principal: principalPayment,
        interest: interestPayment,
        balance: Math.max(0, balance)
      });
    }

    setAmortizationSchedule(schedule);
    setTotalInterest(totalInterestPaid);
  }

  function handleApplyEstimate() {
    if (onEstimateComplete) {
      onEstimateComplete({
        interestRate,
        termLength,
        termType,
        monthlyPayment,
        totalInterest,
        amortizationSchedule
      });
    }
  }

  return (
    <div className="loan-estimator">
      <h3>Loan Estimator</h3>
      
      <div className="estimator-inputs">
        <div className="input-group">
          <label htmlFor="loan-amount">Loan Amount</label>
          <input
            id="loan-amount"
            type="number"
            value={loanAmount}
            disabled
            className="input-field"
          />
        </div>

        <div className="input-group">
          <label htmlFor="interest-rate">Interest Rate (%)</label>
          <input
            id="interest-rate"
            type="number"
            step="0.1"
            min="0"
            max="100"
            value={interestRate}
            onChange={(e) => setInterestRate(e.target.value)}
            className="input-field"
          />
        </div>

        <div className="input-group">
          <label htmlFor="term-length">Payment Term</label>
          <div className="term-input-group">
            <input
              id="term-length"
              type="number"
              min="1"
              value={termLength}
              onChange={(e) => setTermLength(e.target.value)}
              className="input-field term-number"
            />
            <select
              value={termType}
              onChange={(e) => setTermType(e.target.value)}
              className="select-field"
            >
              <option value="months">Months</option>
              <option value="weeks">Weeks</option>
            </select>
          </div>
        </div>
      </div>

      <div className="estimate-summary">
        <div className="summary-card">
          <span className="summary-label">Monthly Payment</span>
          <span className="summary-value">${monthlyPayment.toFixed(2)}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Total Interest</span>
          <span className="summary-value">${totalInterest.toFixed(2)}</span>
        </div>
        <div className="summary-card">
          <span className="summary-label">Total Amount</span>
          <span className="summary-value">${(parseFloat(loanAmount) + totalInterest).toFixed(2)}</span>
        </div>
      </div>

      {amortizationSchedule.length > 0 && (
        <div className="amortization-table-container">
          <h4>Amortization Schedule</h4>
          <div className="table-wrapper">
            <table className="amortization-table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>Payment</th>
                  <th>Principal</th>
                  <th>Interest</th>
                  <th>Balance</th>
                </tr>
              </thead>
              <tbody>
                {amortizationSchedule.map((row) => (
                  <tr key={row.period}>
                    <td>{new Date(row.paymentDate).toLocaleDateString()}</td>
                    <td>${row.payment.toFixed(2)}</td>
                    <td>${row.principal.toFixed(2)}</td>
                    <td>${row.interest.toFixed(2)}</td>
                    <td>${row.balance.toFixed(2)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {onEstimateComplete && (
        <button 
          className="btn btn-primary apply-estimate-btn"
          onClick={handleApplyEstimate}
        >
          Apply These Terms
        </button>
      )}
    </div>
  );
}

export default LoanEstimator;
